import { getDeploymentConfig } from '../services/config';
import { isAirgappedMode } from './env';
import {
  buildURLWithQueryParams,
  isSuccessSigninExternalClientPage,
} from './url';

/**
 * Domains Qodo operates its shared multi-/single-tenant portals under. The bounce
 * is restricted to these so a self-hosted (onprem) deployment on a customer-owned
 * domain is never sent to `app.qodo.ai`, even if its `portal_redirect_url` is left
 * at the multi-tenant default.
 */
const QODO_OPERATED_DOMAIN_SUFFIXES = [
  'qodo.ai',
  'codium.ai',
  'codium-inc.com',
];

/** Whether `hostname` is a Qodo-operated portal host (exact match or subdomain). */
export const isQodoOperatedHost = (hostname: string): boolean =>
  QODO_OPERATED_DOMAIN_SUFFIXES.some(
    (suffix) => hostname === suffix || hostname.endsWith(`.${suffix}`),
  );

/**
 * The deployment's `portal_redirect_url`, parsed — but only when it is safe to
 * navigate to. The field must never be trusted on a non-Qodo-operated host
 * (onprem/airgapped on a customer domain): those deployments may inherit the
 * multi-tenant default (`https://app.qodo.ai/signin`), and navigating there
 * leaks the user off their own portal — or, airgapped, onto a host that does
 * not resolve. Every consumer of `portal_redirect_url` must go through this
 * helper rather than reading the config field directly.
 *
 * Returns `undefined` when the current host is not Qodo-operated, or the field
 * is missing, malformed, or not http(s).
 */
export const getTrustedPortalRedirectUrl = (): URL | undefined => {
  if (!isQodoOperatedHost(window.location.hostname)) {
    return undefined;
  }

  const { portal_redirect_url: portalRedirectUrl } = getDeploymentConfig();

  if (!portalRedirectUrl) {
    return undefined;
  }

  let parsedUrl: URL;

  try {
    parsedUrl = new URL(portalRedirectUrl);
  } catch {
    return undefined;
  }

  const isHttp =
    parsedUrl.protocol === 'https:' || parsedUrl.protocol === 'http:';

  return isHttp ? parsedUrl : undefined;
};

/**
 * Resolves the canonical sign-in URL an unauthenticated user on a satellite
 * (single-tenant) portal must be bounced to, or `undefined` when the current
 * portal is already canonical and no bounce applies.
 *
 * Interactive OAuth (Google/GitHub/SSO) is only registered against the canonical
 * portal origin, so starting sign-in on any other origin fails with
 * `redirect_uri_mismatch`. `portal_redirect_url` — the deployment's canonical
 * sign-in URL, the same field sign-out uses — names that origin; when it differs
 * from the current origin the current portal is a satellite and must bounce. The
 * current query string is preserved so post-login context (return_url, state,
 * init_client, …) survives the hop.
 *
 * Returns `undefined` for airgapped deployments, the CLI device-login success
 * page (a legitimate single-tenant landing via the 409 flow), an untrusted
 * `portal_redirect_url` (see `getTrustedPortalRedirectUrl`), or when its origin
 * already matches the current one.
 */
export const getCanonicalPortalSignInRedirect = (): string | undefined => {
  if (isAirgappedMode() || isSuccessSigninExternalClientPage()) {
    return undefined;
  }

  const canonicalUrl = getTrustedPortalRedirectUrl();

  if (!canonicalUrl || canonicalUrl.origin === window.location.origin) {
    return undefined;
  }

  return buildURLWithQueryParams(
    canonicalUrl.toString(),
    new URLSearchParams(window.location.search),
  );
};
